import { useState } from "react";
import { createPortal } from "react-dom";
import { LOGO_B64 } from "./SalesReports";
import { htmlToPdfBlob, safeFilename } from "../pdfRenderer";

export const REJECTION_IMPACT_NOTE_ES = "El presente rechazo tendría un impacto directo en el retorno de este lote y en su precio final.";

var GFI = {
  name: "Ground Fresh International, Inc.",
  lines: ["152 W Hoffman Ave Ste 5", "Lindenhurst, NY 11757"],
};

function escHtml(s) {
  return String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

export function rejectedCasesForInvoice(invoice) {
  return (invoice && invoice.rejections || []).reduce(function (s, rj) { return s + (parseInt(rj.cases, 10) || 0); }, 0);
}

export function rejectedCasesForLot(lot, invoices) {
  return (invoices || []).filter(function (inv) { return String(inv.lot || "") === String(lot || ""); })
    .reduce(function (s, inv) { return s + rejectedCasesForInvoice(inv); }, 0);
}

export function RejectionDetailsReadOnly({ invoices, lot, lang }) {
  var rows = [];
  (invoices || []).filter(function (inv) { return String(inv.lot || "") === String(lot || ""); }).forEach(function (inv) {
    (inv.rejections || []).forEach(function (rj, idx) {
      if (!(parseInt(rj.cases, 10) > 0)) return;
      rows.push({ pu: inv.puNumber || "—", n: idx + 1, cases: rj.cases, notes: rj.notes || "", clientAt: rj.clientNotifiedAt, sentAt: rj.sentAt });
    });
  });
  if (rows.length === 0) return null;
  var fmt = function (v) { return v ? new Date(v).toLocaleString() : "—"; };
  return <RejectionsCollapsible rows={rows} lang={lang} fmt={fmt} />;
}

function RejectionsCollapsible({ rows, lang, fmt }) {
  var [open, setOpen] = useState(false);
  return (
    <div style={{ marginBottom: 14, borderRadius: 8, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.04)" }}>
      <button type="button" onClick={function () { setOpen(!open); }} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "transparent", border: "none", cursor: "pointer", color: "#B91C1C", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em" }}>
        <span>{"⚠ Rejections"}</span>
        <span>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div style={{ padding: "0 12px 10px" }}>
      {rows.map(function (r, i) {
        return (
          <div key={i} style={{ fontSize: 12, padding: "6px 0", borderTop: i > 0 ? "1px solid rgba(220,38,38,0.15)" : "none" }}>
            <div><strong>{"PU# " + r.pu + " · Rejection " + r.n}</strong>{" — " + r.cases + " " + (lang === "es" ? "cajas" : "cases")}</div>
            {r.notes && <div style={{ marginTop: 3, whiteSpace: "pre-line" }}>{r.notes}</div>}
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3 }}>{(lang === "es" ? "Cliente me notificó: " : "Client notified me: ") + fmt(r.clientAt) + " · " + (lang === "es" ? "Nota enviada al shipper: " : "Note sent to shipper: ") + fmt(r.sentAt)}</div>
          </div>
        );
      })}
      </div>}
    </div>
  );
}

export function RejectionBadge({ cases }) {
  if (!cases) return null;
  return (
    <span title={"Client rejection · " + cases + " cases"} style={{ display: "inline-block", width: 0, overflow: "visible", whiteSpace: "nowrap", transform: "translateX(4px)", fontSize: 11, lineHeight: 1, color: "#DC2626", fontWeight: 700 }}>⚠</span>
  );
}

export function rejectionNoticeSentence(rejection) {
  var total = parseInt(rejection && rejection.cases, 10) || 0;
  return "Hemos recibido una notificación de rechazo a nivel retailer por " + total + " cajas. Favor de consultar los comentarios sobre las acciones tomadas y las fotografías adjuntas. " + REJECTION_IMPACT_NOTE_ES;
}

function productText(invoice) {
  return (invoice.lines || []).map(function (l) {
    return l.productDescription || [l.productBase || l.productName, l.size, l.category].filter(Boolean).join(" ");
  }).filter(Boolean).join(", ") || "—";
}

export function buildRejectionReportHTML(invoice, shipment, supplier, company, notifiedAt, rejection) {
  var totalCases = parseInt(rejection && rejection.cases, 10) || 0;
  var shipperName = (supplier && (supplier.fullName || supplier.shortName)) || "—";
  var shipperAddrParts = supplier ? [supplier.address, [supplier.city, supplier.state].filter(Boolean).join(", "), supplier.country].filter(Boolean) : [];
  var fmtDT = function (v) { return v ? new Date(v).toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"; };
  var clientNotifiedText = fmtDT(rejection && rejection.clientNotifiedAt);
  var shipperNotifiedText = fmtDT(notifiedAt);

  var h = '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Rejection Notice — ' + escHtml(invoice.puNumber || "") + '</title><style>';
  h += '@page{size:letter;margin:0.55in 0.6in}';
  h += '*{margin:0;padding:0;box-sizing:border-box}';
  h += 'body{font-family:"Segoe UI",Arial,sans-serif;font-size:10px;color:#1a1a1a;line-height:1.4;padding-top:14px}';
  h += '.page{max-width:700px;margin:0 auto}';
  h += '.header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:3px solid #1B3A4B}';
  h += '.logo img{height:100px}';
  h += '.doc-title{font-size:24px;font-weight:300;color:#1B3A4B;letter-spacing:2px;text-align:right}';
  h += '.doc-sub{font-size:10px;color:#666;text-align:right;margin-top:4px}';
  h += '.parties{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin:14px 0}';
  h += '.party{padding:10px 14px;border:1px solid #e0e0e0;border-radius:6px;background:#fafbfc}';
  h += '.party .p-label{font-size:8px;text-transform:uppercase;letter-spacing:1px;color:#888;font-weight:700;margin-bottom:4px}';
  h += '.party .p-name{font-size:13px;font-weight:700;color:#1B3A4B;margin-bottom:2px}';
  h += '.party .p-addr{font-size:9px;color:#666;line-height:1.6}';
  h += '.meta-grid{display:grid;grid-template-columns:repeat(6,1fr);gap:10px;margin:0 0 12px;padding:12px 14px;background:#f7f8fa;border-radius:6px;border-left:4px solid #1B3A4B;border-right:4px solid #1B3A4B}';
  h += '.meta-grid .label{font-size:7.5px;text-transform:uppercase;letter-spacing:0.5px;color:#888;font-weight:700}';
  h += '.meta-grid .value{font-size:11px;font-weight:600;color:#1a1a1a;margin-top:1px}';
  h += '.notice-box{margin:0 0 14px;padding:12px 14px;background:#FFF8E1;border:1px solid #F5C244;border-radius:6px;font-size:10px;color:#6b4e00;white-space:pre-line}';
  h += '.notes-box{margin:0 0 14px;padding:12px 14px;background:#fff;border:1px solid #d0d5dd;border-radius:6px;font-size:10px;white-space:pre-line}';
  h += '.notes-box .box-title{font-size:9px;text-transform:uppercase;letter-spacing:0.8px;font-weight:700;color:#1B3A4B;margin-bottom:6px}';
  h += '.totals{margin-top:10px;padding:8px 14px;background:#1B3A4B;color:#fff;border-radius:6px;display:flex;justify-content:space-between;font-size:10px;font-weight:600}';
  h += '.footer{margin-top:22px;padding-top:10px;border-top:1px solid #ddd;font-size:8px;color:#999;text-align:center}';
  h += '</style></head><body><div class="page">';
  h += '<script>try{window.scrollTo(0,0);document.documentElement.scrollTop=0;document.body.scrollTop=0;}catch(e){}</script>';

  h += '<div class="header"><div class="logo"><img src="' + LOGO_B64 + '" /></div>';
  h += '<div><div class="doc-title">NOTA DE RECHAZO</div>';
  h += '<div class="doc-sub">Notificado por el cliente: ' + escHtml(clientNotifiedText) + '</div>';
  h += '<div class="doc-sub">Notificado al shipper: ' + escHtml(shipperNotifiedText) + '</div></div></div>';

  h += '<div class="parties">';
  h += '<div class="party"><div class="p-label">De</div><div class="p-name">' + escHtml(GFI.name) + '</div>';
  h += '<div class="p-addr">' + GFI.lines.map(escHtml).join("<br/>") + '</div></div>';
  h += '<div class="party"><div class="p-label">Para (Shipper)</div><div class="p-name">' + escHtml(shipperName) + '</div>';
  if (shipperAddrParts.length) h += '<div class="p-addr">' + shipperAddrParts.map(escHtml).join("<br/>") + '</div>';
  h += '</div></div>';

  h += '<div class="meta-grid">';
  h += '<div><div class="label">PU#</div><div class="value">' + escHtml(invoice.puNumber || "—") + '</div></div>';
  h += '<div><div class="label">PO#</div><div class="value">' + escHtml(invoice.clientRef || "—") + '</div></div>';
  h += '<div><div class="label">Factura Shipper #</div><div class="value">' + escHtml((shipment && shipment.shipperInvoice) || "—") + '</div></div>';
  h += '<div><div class="label">Proforma #</div><div class="value">' + escHtml((shipment && shipment.proforma) || "—") + '</div></div>';
  h += '<div><div class="label">Lote</div><div class="value">' + escHtml(invoice.lot || (shipment && shipment.lot) || "—") + '</div></div>';
  h += '<div><div class="label">Producto</div><div class="value">' + escHtml(productText(invoice)) + '</div></div>';
  h += '</div>';

  h += '<div class="notice-box">' + escHtml(rejectionNoticeSentence(rejection)) + '</div>';

  if (rejection && rejection.notes) {
    h += '<div class="notes-box"><div class="box-title">Notas del cliente</div>' + escHtml(rejection.notes) + '</div>';
  }

  h += '<div class="totals"><span>Cajas rechazadas: ' + totalCases + '</span><span>Lote: ' + escHtml(invoice.lot || (shipment && shipment.lot) || "—") + '</span></div>';

  h += '<div class="footer">Ground Fresh International, Inc. · 152 W Hoffman Ave Ste 5, Lindenhurst, NY 11757 · ground-fresh.com</div>';
  h += '</div></body></html>';
  return h;
}

export function buildRejectionReportBlob(invoice, shipment, supplier, company, notifiedAt, rejection) {
  return htmlToPdfBlob(buildRejectionReportHTML(invoice, shipment, supplier, company, notifiedAt, rejection));
}

export function rejectionReportFilename(invoice, shipment, supplier) {
  var shipperName = (supplier && (supplier.fullName || supplier.shortName)) || "";
  var shipperInvoice = (shipment && shipment.shipperInvoice) || "";
  return safeFilename("Rejection Notice " + (invoice.puNumber || "") + " " + shipperInvoice + " " + shipperName) + ".pdf";
}

export function RejectionReportPreview({ invoice, shipment, supplier, company, notifiedAt, rejection, onClose }) {
  var html = buildRejectionReportHTML(invoice, shipment, supplier, company, notifiedAt, rejection);
  var [pdfBusy, setPdfBusy] = useState(false);
  var handleDownloadPdf = async function () {
    setPdfBusy(true);
    try {
      var blob = await buildRejectionReportBlob(invoice, shipment, supplier, company, notifiedAt, rejection);
      var url = URL.createObjectURL(blob);
      var a = document.createElement("a");
      a.href = url;
      a.download = rejectionReportFilename(invoice, shipment, supplier);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("PDF error: " + (e.message || String(e)));
    } finally {
      setPdfBusy(false);
    }
  };
  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 20002, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 60, overflowY: "auto" }}>
      <div style={{ width: 800, maxWidth: "97vw", background: "#fff", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden", marginBottom: 40 }}>
        <div style={{ padding: "10px 20px", background: "var(--color-bg-card-solid)", borderBottom: "1px solid var(--color-border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 14, fontWeight: 700 }}>Rejection Notice — PU# {invoice.puNumber}</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleDownloadPdf} disabled={pdfBusy} style={{ padding: "6px 16px", borderRadius: 6, border: "1px solid #1B3A4B", background: "transparent", color: "#1B3A4B", cursor: pdfBusy ? "wait" : "pointer", fontSize: 12, fontWeight: 600 }}>{pdfBusy ? "…" : "💾 Download"}</button>
            <button onClick={onClose} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", cursor: "pointer", fontSize: 12 }}>Close</button>
          </div>
        </div>
        <div style={{ padding: 0, background: "#fff" }}>
          <iframe
            title="rejection-report"
            srcDoc={html}
            style={{ width: "100%", height: "calc(100vh - 110px)", minHeight: 500, border: "none", display: "block" }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}
